import type { SyntheticEvent } from 'react';
import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';
import { useCatalogFilters } from '@/packages/courses/context/catalog-filters';
import type {
  FilterOption,
  FilterSchemaSelect,
} from '@/packages/courses/types/schema';
import type { CatalogUrlKey } from '@/packages/courses/types/url-params';

interface FilterSelectProps extends FilterSchemaSelect {
  id: CatalogUrlKey;
}

export const FilterSelect = ({
  id,
  title,
  placeholder,
  multiple = false,
  options,
}: FilterSelectProps) => {
  const { params, actions } = useCatalogFilters();
  const selectedValues = params[id] ?? [];
  const selected = options.filter((o) => selectedValues.includes(o.value));

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    value: FilterOption | FilterOption[] | null
  ) => {
    const arr = Array.isArray(value) ? value : value != null ? [value] : [];
    const next = arr.map((o) => o.value);
    actions.setParam(id, next.length ? next : undefined);
  };

  const labelId = `${id}-label`;

  return (
    <FormControl fullWidth aria-labelledby={labelId}>
      <FormLabel id={labelId} htmlFor={id}>
        {title}
      </FormLabel>
      <Autocomplete
        multiple={multiple}
        options={options}
        value={multiple ? selected : (selected[0] ?? null)}
        getOptionLabel={(opt) => opt.label}
        getOptionKey={(opt) => opt.value}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        onChange={handleChange}
        renderInput={(renderParams) => (
          <TextField
            {...renderParams}
            id={id}
            slotProps={{
              htmlInput: {
                ...renderParams.inputProps,
                'aria-labelledby': labelId,
              },
            }}
            placeholder={selected.length === 0 ? placeholder : undefined}
            size="small"
          />
        )}
      />
    </FormControl>
  );
};
